function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

/**
 * Determines whether the specified URL is absolute
 *
 * @param {string} url The URL to test
 * @returns {boolean} True if the specified URL is absolute, otherwise false
 */
function isAbsoluteURL(url) {
  // A URL is considered absolute if it begins with "<scheme>://" or "//" (protocol-relative URL).
  // RFC 3986 defines scheme name as a sequence of characters beginning with a letter and followed
  // by any combination of letters, digits, plus, period, or hyphen.
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}
/**
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 * @returns {string} The combined URL
 */

function combineURLs(baseURL, relativeURL) {
  return relativeURL ? baseURL.replace(/\/+$/, '') + '/' + relativeURL.replace(/^\/+/, '') : baseURL;
}
/**
 * Creates a new URL by combining the baseURL with the requestedURL,
 * only when the requestedURL is not already an absolute URL.
 * If the requestURL is absolute, this function returns the requestedURL untouched.
 *
 * @param {string} baseURL The base URL
 * @param {string} requestedURL Absolute or relative URL to combine
 * @returns {string} The combined full path
 */

function buildFullPath(baseURL, requestedURL) {
  if (baseURL && !isAbsoluteURL(requestedURL)) {
    return combineURLs(baseURL, requestedURL);
  }

  return requestedURL;
}
function getFullUrlByConf(config) {
  return buildFullPath(config.baseURL, config.url);
}

/**
 * 加密算法的版本
 * 由于前后使用的是对称加密算法,为了在同时多套算法共存情况下保证加密算法的一致性
 */

const SIGN_VERSION = "0.1";

function defaultSign(url, ts) {
  return `${url}${ts}`;
}

class RequestSignInterceptor {
  /**
   * 时间偏移量
   */

  /**
   * 是否在刷新时间
   */

  /**
   * 刷新时间任务
   */

  /**
   * 获取偏移量
   */
  get timeOffset() {
    return this._timeOffset;
  }
  /**
   * 设置偏移量
   */


  set timeOffset(value) {
    this._timeOffset = value;
    this.setLocalTimeOffset(this._timeOffset);
  }
  /**
   * performance.now() 不会因为使用过程中用户操作系统时间同步突然完成而发生大幅度突变，Date.now() 会
   */


  set supposedAccurateTimeBase(ts) {
    this._supposedAccurateTimeBase = ts;
    this._timebaseOffset = prefNow();
  }

  get supposedAccurateTime() {
    return this._supposedAccurateTimeBase + (prefNow() - this._timebaseOffset);
  }
  /**
   * 存储时间偏移量的LocalStorage的key
   */


  get timeOffsetStorageKey() {
    return this.options.timeOffsetStorageKey || "patsnap-time-offset";
  }

  constructor(options) {
    this.options = options;

    _defineProperty(this, "_timeOffset", 0);

    _defineProperty(this, "isRefreshIng", false);

    _defineProperty(this, "refreshTimePromise", Promise.resolve());

    _defineProperty(this, "_supposedAccurateTimeBase", Date.now());

    _defineProperty(this, "_timebaseOffset", prefNow());

    this._timeOffset = this.getLocalTimeOffset();
    this.supposedAccurateTimeBase = Date.now() + this.timeOffset;
  }
  /**
   * 请求拦截器
   * @param config 请求配置
   * @returns
   */


  async requestInterceptor(config) {
    if (this.isRefreshIng) await this.refreshTimePromise;
    const fullUrl = getFullUrlByConf(config);

    if (this.options.detectShouldSignRequest && !this.options.detectShouldSignRequest(config, fullUrl)) {
      return config;
    }

    try {
      const XTs = this.supposedAccurateTime;
      const XSign = await this.sign(fullUrl, XTs);
      config.headers = { ...(config.headers || {}),
        "x-t": XTs,
        "x-p-s": XSign // "x-sign-version": SIGN_VERSION,

      };
    } catch (error) {
      console.error(error);
    }

    return config;
  }

  async sign(url, ts) {
    return defaultSign(url, ts);
  }
  /**
   * 响应拦截器
   * @param response 响应
   * @param requester 请求器
   * @returns
   */


  async responseInterceptor(requester, response) {
    if (this.options.detectResponseShouldRefresh(response)) {
      await this.syncServiceTime();
      return requester.request(response.config);
    }

    return response;
  }

  async responseErrorInterceptor(requester, error) {
    if (this.options.detectResponseShouldRefresh(error)) {
      await this.syncServiceTime();
      return requester.request(error.config);
    }

    return Promise.reject(error);
  }
  /**
   * 同步服务器时间
   */


  syncServiceTime() {
    this.isRefreshIng = true;
    this.refreshTimePromise = this.options.getServiceTimeStamp().then(timestamp => {
      this.supposedAccurateTimeBase = timestamp;
      this.timeOffset = timestamp - Date.now();
    }).catch(error => this.options.onGetServiceTimeStampError && this.options.onGetServiceTimeStampError(error)).finally(() => {
      this.isRefreshIng = false;
    });
    return this.refreshTimePromise;
  }
  /**
   * 获取时间偏移量
   * @returns number
   */


  getLocalTimeOffset() {
    try {
      const stringValue = localStorage.getItem(this.timeOffsetStorageKey);

      if (!stringValue) {
        return 0;
      }

      return Number(stringValue);
    } catch (error) {
      return 0;
    }
  }
  /**
   * 存储时间偏移
   * @param value 时间戳
   */


  setLocalTimeOffset(value) {
    try {
      localStorage.setItem(this.timeOffsetStorageKey, `${value}`);
    } catch (error) {
      console.error(error);
    }
  }

}

function registerRequestSignInterceptor(axiosInstance, options) {
  const requestSignInterceptor = new RequestSignInterceptor(options);
  const requestInterceptor = requestSignInterceptor.requestInterceptor.bind(requestSignInterceptor);
  const responseInterceptor = requestSignInterceptor.responseInterceptor.bind(requestSignInterceptor, axiosInstance);
  const responseErrorInterceptor = requestSignInterceptor.responseErrorInterceptor.bind(requestSignInterceptor, axiosInstance);
  axiosInstance.interceptors.request.use(requestInterceptor);
  axiosInstance.interceptors.response.use(options.interceptorSuccessResponse ? responseInterceptor : undefined, responseErrorInterceptor);
  return requestSignInterceptor;
}

function prefNow() {
  return Math.floor(performance.now());
}

export { SIGN_VERSION, registerRequestSignInterceptor };
//# sourceMappingURL=RequestSignInterceptor.mjs.map
